import React from "react";
import { isNil } from "lodash";
import { IssueCustomAttributeType } from "@adsk/acc-issues-types";
import type {
  IssueCustomAttribute,
  IssueCustomAttributeDefinition,
} from "@adsk/acc-issues-types";
import { getCustomFieldEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFields";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useLocalizedDecimalSeparator } from "@adsk/acc-issues-core/utils/hooks/useLocalizedDecimalSeparator";
import { EditableFieldWrapper } from "@adsk/acc-issues-common/components/primitive/EditableFieldWrapper";
import { IssueCustomListDropdown } from "@adsk/acc-issues-common/components/input/IssueCustomListDropdown";
import { AtomicCustomField } from "./AtomicCustomField";

export const CustomFieldInForm = ({
  attribute,
  value,
  displayValue,
  containerRef,
  handleCustomAttributeChange,
  isRequired,
  disabled,
  viewOnly = false,
}: {
  attribute: IssueCustomAttributeDefinition;
  value: string | number | boolean | undefined;
  displayValue: string | number | boolean | undefined;
  containerRef?: React.MutableRefObject<null>;
  handleCustomAttributeChange: (value: any) => void;
  isRequired: boolean;
  disabled: boolean;
  viewOnly?: boolean;
}) => {
  const { formatLocalizedNumber, onChangeLocalizedNumber } =
    useLocalizedDecimalSeparator();
  const { T } = useTranslations();

  if (!attribute) {
    return null;
  }

  switch (attribute.dataType) {
    case IssueCustomAttributeType.List:
      return (
        <EditableFieldWrapper
          testId="cf-list-field"
          fieldKey={`${attribute.dataType}-${attribute.id}`}
          label={attribute.title}
          value={(isNil(displayValue) ? "" : displayValue).toString()}
          disabled={disabled}
          containerRef={containerRef}
          renderInput={({ onChange: inlineEditOnChange }) => (
            <IssueCustomListDropdown
              attribute={attribute}
              required={isRequired}
              onChange={value => {
                handleCustomAttributeChange(value);
                inlineEditOnChange(value || undefined);
              }}
              onClose={() => inlineEditOnChange(value)}
              value={value as string}
              defaultIsOpen
            />
          )}
          required={isRequired}
          showDisabledReason={!viewOnly}
          infoTooltipContent={attribute.description}
        />
      );
    case IssueCustomAttributeType.Numeric:
      displayValue = formatLocalizedNumber(displayValue);
      const onChange = onChangeLocalizedNumber(handleCustomAttributeChange);

      return (
        <AtomicCustomField
          testId="cf-numeric-field"
          attribute={attribute}
          value={displayValue}
          containerRef={containerRef}
          handleCustomAttributeChange={onChange}
          isRequired={isRequired}
          disabled={disabled}
          viewOnly={viewOnly}
          placeholder={
            T.translate("customFieldsAdmin.placeholder.numeric") as string
          }
        />
      );
    case IssueCustomAttributeType.Text:
      return (
        <AtomicCustomField
          testId="cf-text-field"
          attribute={attribute}
          value={value}
          containerRef={containerRef}
          handleCustomAttributeChange={handleCustomAttributeChange}
          isRequired={isRequired}
          disabled={disabled}
          viewOnly={viewOnly}
          placeholder={
            T.translate("customFieldsAdmin.placeholder.text") as string
          }
        />
      );
    case IssueCustomAttributeType.Paragraph:
      return (
        <AtomicCustomField
          testId="cf-paragraph-field"
          attribute={attribute}
          value={value}
          containerRef={containerRef}
          handleCustomAttributeChange={handleCustomAttributeChange}
          isRequired={isRequired}
          disabled={disabled}
          viewOnly={viewOnly}
          placeholder={
            T.translate("customFieldsAdmin.placeholder.text") as string
          }
        />
      );
    default:
      return null;
  }
};

export function getCustomAttributeDisplayValue(
  attribute: IssueCustomAttributeDefinition,
  value?: IssueCustomAttribute["value"]
) {
  if (!value) {
    return value;
  }

  if (attribute && attribute.dataType === IssueCustomAttributeType.List) {
    const option = attribute.metadata.list.options.find(
      ({ id }) => id === value
    );

    return option ? option.value : "";
  }

  return value;
}

export function useCustomAttributeBasedOnAttributeId(
  attributeDefinitionId: string
) {
  const issueCustomFieldEntitiesMap = useSelector(getCustomFieldEntitiesMap);
  const attribute: IssueCustomAttributeDefinition =
    issueCustomFieldEntitiesMap[attributeDefinitionId];

  return attribute;
}
